import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@env/environment';

export interface BuildInfo {
  build: string;
  buildDate: string;
  version: string;
}

@Injectable({
  providedIn: 'root'
})
export class BuildService {
  private buildInfo: BuildInfo | null = null;

  constructor(private http: HttpClient) {}

  private getFallbackInfo(): BuildInfo {
    return {
      build: new Date().toISOString().split('T')[0].replace(/-/g, '.'),
      buildDate: new Date().toISOString(),
      version: '0.0.0.0'
    };
  }

  getBuildInfo(): Observable<BuildInfo> {
    if (this.buildInfo) {
      return of(this.buildInfo);
    }

    // In development mode, use fallback information instead of reading build.json
    if (environment.production === false) {
      console.log('Development mode detected, using fallback build info');
      this.buildInfo = this.getFallbackInfo();
      return of(this.buildInfo);
    }

    // In production mode, try to load from build.json
    return this.http.get<BuildInfo>('/assets/build.json').pipe(
      map(info => {
        this.buildInfo = info;
        return info;
      }),
      catchError(error => {
        console.warn(
          'Could not load build.json, using fallback build info:',
          error
        );
        this.buildInfo = this.getFallbackInfo();
        return of(this.buildInfo);
      })
    );
  }

  getBuildNumber(): Observable<string> {
    return this.getBuildInfo().pipe(map(info => info.build));
  }

  getBuildDate(): Observable<string> {
    return this.getBuildInfo().pipe(map(info => info.buildDate));
  }

  getVersion(): Observable<string> {
    return this.getBuildInfo().pipe(map(info => info.version));
  }
} 